﻿import Phaser from 'phaser'
import { MatchEngine } from '@engine/MatchEngine'
import { findPath } from '@engine/Pathfinder'
import type { TowerCard, PlacedTower, Deck, MapConfig, Player, UnitCard, Projectile, GridCoord } from '@interfaces/index'
import { getUIBridge } from '@game/UIBridge'
import { GridRenderer } from '@game/renderers/GridRenderer'
import { TowerRenderer } from '@game/renderers/TowerRenderer'
import { UnitRenderer } from '@game/renderers/UnitRenderer'
import { ProjectileRenderer } from '@game/renderers/ProjectileRenderer'
import { TouchInput } from '@game/input/TouchInput'
import { KeyboardInput } from '@game/input/KeyboardInput'
import type { GameDataService } from '../../services/GameDataService'
import type { QuestDefinition, QuestState as _QuestState } from '@engine/QuestSystem'
import { QuestSystem } from '@engine/QuestSystem'
import { LocalBotTransport } from '../../adapters/LocalBotTransport'
import { WebSocketTransport } from '../../adapters/WebSocketTransport.js'
import type { ITransport } from '../../interfaces/index'
import { soundManager } from '../SoundManager.js'
import { NotificationService } from '../../services/NotificationService.js'
import { cartToIso } from '@engine/Grid'
import i18n from '../../i18n.js'
import { PerformanceMonitor } from '@engine/PerformanceMonitor.js'

function pickBotName(): string {
  const names = i18n.t('BOT_NAMES', { returnObjects: true }) as string[]
  return Array.isArray(names) && names.length > 0
    ? (names[Date.now() % names.length] ?? names[0]!)
    : 'Bot'
}

interface MatchSceneInitData {
  gameData: GameDataService
  questDefinitions?: QuestDefinition[]
  questSystem?: QuestSystem
}

interface TowerStatMultipliers {
  dmgMult: number
  spdMult: number
  rangeMult: number
}

interface StartMatchDetail {
  deckId: string | null
  mapId: string
  towerStatOverrides?: Record<string, TowerStatMultipliers>
  /** Campaign: override bot personality (e.g. 'rusher', 'strategist'). */
  botPersonality?: string
  /** Campaign: override starting bot tier (1–3). */
  botTier?: number
}

function buildTestTower(
  gx: number,
  gy: number,
  card: TowerCard,
  overrides?: { dmgMult: number; spdMult: number; rangeMult: number }
): PlacedTower {
  const dmgMult = overrides?.dmgMult ?? 1
  const spdMult = overrides?.spdMult ?? 1
  const rangeMult = overrides?.rangeMult ?? 1
  return {
    id: card.id,
    instanceId: `${card.id}-${gx}-${gy}-${Date.now()}`,
    name: card.name,
    el: card.el,
    cat: card.cat,
    cost: card.cost,
    col: card.el,
    gx,
    gy,
    level: 1,
    path: null,
    lastShot: 0,
    dmg: card.stats.dmg * dmgMult,
    spd: card.stats.spd * spdMult,
    range: card.stats.range * rangeMult,
    crit: card.stats.crit,
    splash: card.stats.splash,
    slow: card.stats.slow,
    chain: card.stats.chain,
    income: card.stats.income,
    terrain: card.stats.terrain,
    projSpeed: card.stats.projSpeed,
    upgrades: card.upgrades,
  }
}

/** Consume the transport stored by MatchmakingScreen, if any. */
function consumeWindowTransport(): ITransport | null {
  const win = window as unknown as Record<string, unknown>
  const t = win['__wsTransport']
  if (t != null) {
    delete win['__wsTransport']
    return t as ITransport
  }
  return null
}

/**
 * Consume the server-assigned localPlayerId stored by MatchmakingScreen.
 * Falls back to 'player1' for bot matches (where no server assignment exists).
 */
function consumeWindowPlayerId(): string {
  const win = window as unknown as Record<string, unknown>
  const id = win['__localPlayerId']
  if (typeof id === 'string' && id.length > 0) {
    delete win['__localPlayerId']
    return id
  }
  return 'player1'
}

export class MatchScene extends Phaser.Scene {
  private engine!: MatchEngine
  private gridRenderer!: GridRenderer
  private towerRenderer!: TowerRenderer
  private unitRenderer!: UnitRenderer
  private projectileRenderer!: ProjectileRenderer
  private touchInput!: TouchInput
  private keyboardInput!: KeyboardInput
  private questSystem: QuestSystem | null = null
  private localPlayerId = 'player1'
  private opponentPlayerId = 'player2'
  private selectedTowerId: string | null = null

  private gameData!: GameDataService
  private initData!: MatchSceneInitData
  private spyMode = false
  private spyOverlay!: Phaser.GameObjects.Graphics
  // Wave timer tracking — mirrors bot send interval to show countdown in HUD
  private waveTimerMs = 0
  private waveIntervalMs = 15000 // matches botConfig.initialIntervalMs default

  // Match-end guard — set to true when MATCH_END fires; update() exits early
  private _matchEnded = false

  // Tower stat multipliers from card progression (set per match via ultimaze:start-match event)
  private _towerStatOverrides: Record<string, TowerStatMultipliers> = {}

  // Coop mode: secondary WebSocket transport for partner HP relay.
  // null in all non-coop modes.
  private _coopWsTransport: WebSocketTransport | null = null
  // Cleanup function for coop partner-defeated DOM listener
  private _coopPartnerDefeatedCleanup: (() => void) | null = null
  // Cleanup functions for server-driven coop wave DOM listeners
  private _coopWaveStartCleanup: (() => void) | null = null
  private _coopVictoryCleanup: (() => void) | null = null
  // Phaser overlay container for coop wave announcements (destroyed each match)
  private _coopWaveOverlay: Phaser.GameObjects.Container | null = null

  // ── Performance overlay (F key / triple-tap to toggle) ────────────────────
  private _perfOverlay: Phaser.GameObjects.Text | null = null
  private _perfVisible = false
  private _frameCount = 0
  private _fpsAccum = 0
  private _lastFpsSample = 0
  private _tripleTapCount = 0
  private _tripleTapTimer = 0
  // ── Adaptive render quality ────────────────────────────────────────────────
  // Sampled every 2 s regardless of perf-overlay visibility.
  // < 28 fps → 'compact', > 50 fps → back to 'full'.
  private _renderQuality: 'full' | 'compact' = 'full'
  private _qualityFrameCount = 0
  private _qualityFpsAccum = 0
  private _qualityLastSample = 0

  // ── Projectile visual system ───────────────────────────────────────────────
  // Projectiles are purely visual: engine damage is applied immediately,
  // these are cosmetic representations flying from tower to unit.
  private projectiles: Projectile[] = []
  // Cache of tower lastShot timestamps so we can detect when a shot fires
  private towerLastShotCache: Map<string, number> = new Map()

  constructor() {
    super({ key: 'MatchScene' })
  }

  init(data: MatchSceneInitData): void {
    this.initData = data
  }

  create(): void {
    // Require validated gameData — no fallback to hardcoded data
    if (this.initData === undefined || this.initData.gameData === undefined) {
      throw new Error(
        '[MatchScene] gameData was not passed from BootScene. ' +
        'Ensure BootScene calls GameDataService.getInstance().loadFromCache() ' +
        'and passes { gameData: GameDataService.getInstance() } to this.scene.start().'
      )
    }

    this.gameData = this.initData.gameData
    this.questSystem = this.initData.questSystem ?? null

    // Listen for start-match event from React (carries deckId, mapId and progression overrides)
    const onStartMatch = (e: Event): void => {
      const detail = (e as CustomEvent<StartMatchDetail>).detail
      this._initEngine(detail.mapId ?? 'map-vulkan', detail.deckId ?? null, detail.towerStatOverrides ?? {}, detail.botPersonality, detail.botTier)
    }
    window.addEventListener('ultimaze:start-match', onStartMatch)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('ultimaze:start-match', onStartMatch)
    })

    // Handle the rare race where React dispatched ultimaze:start-match before this
    // listener was registered (e.g. very fast initial navigation to 'game' screen).
    // The React dispatch code stores the detail on window.__pendingMatchStart first.
    const pending = (window as unknown as Record<string, unknown>)['__pendingMatchStart'] as StartMatchDetail | undefined
    if (pending !== undefined) {
      delete (window as unknown as Record<string, unknown>)['__pendingMatchStart']
      this._initEngine(pending.mapId ?? 'map-vulkan', pending.deckId ?? null, pending.towerStatOverrides ?? {}, pending.botPersonality, pending.botTier)
    }

    // Defer AudioContext creation until first user gesture (browser autoplay policy)
    this.input.once('pointerdown', () => soundManager.init())

    // Touch/pointer input — tap to place tower or open upgrade panel
    this.touchInput = new TouchInput(this, {
      onTap: (x, y) => {
        if (!this.engine) return

        // Triple-tap anywhere to toggle the FPS overlay (mobile shortcut)
        const now = this.time.now
        if (now - this._tripleTapTimer < 600) {
          this._tripleTapCount++
          if (this._tripleTapCount >= 3) {
            this._tripleTapCount = 0
            this._togglePerfOverlay()
            return
          }
        } else {
          this._tripleTapCount = 1
        }
        this._tripleTapTimer = now

        // x, y are screen-space; transform to world-space to account for camera zoom/pan
        const worldPoint = this.cameras.main.getWorldPoint(x, y)
        const cell = this.gridRenderer.pointerToGrid(worldPoint.x, worldPoint.y)
        if (cell === null) return

        const grid = this.engine.getGrid(this.localPlayerId)
        const existingTower = grid?.getTowerAt(cell.gx, cell.gy)

        if (this.selectedTowerId !== null) {
          // Place tower mode — apply progression stat multipliers if available
          const card = this.gameData.towers.find((t) => t.id === this.selectedTowerId)
          if (card === undefined) return
          const overrides = this._towerStatOverrides[card.id]
          const tower = buildTestTower(cell.gx, cell.gy, card, overrides)
          this.engine.placeTower(this.localPlayerId, cell.gx, cell.gy, tower)
          // Tower stays selected — player cancels via X button in HUD
        } else if (existingTower !== undefined) {
          // Tap on existing tower → show upgrade panel + range ring
          window.dispatchEvent(new CustomEvent('ultimaze:show-upgrade', {
            detail: {
              instanceId: existingTower.instanceId,
              gx: cell.gx,
              gy: cell.gy,
              name: existingTower.name,
              level: existingTower.level,
              path: existingTower.path,
              upgrades: existingTower.upgrades,
              range: existingTower.range,
            }
          }))
        }
      },
    })

    // Keyboard shortcuts
    this.keyboardInput = new KeyboardInput(this, [
      {
        key: 'R',
        label: 'Rotate camera view',
        callback: () => {
          this.gridRenderer.rotateStep()
        },
      },
      {
        key: 'Q',
        label: 'Cast spell',
        callback: () => {
          if (this.engine && typeof (this.engine as unknown as Record<string, unknown>)['castSpell'] === 'function') {
            (this.engine as unknown as { castSpell(pid: string): void }).castSpell(this.localPlayerId)
          }
        },
      },
      {
        key: 'F',
        label: 'Toggle FPS performance overlay',
        callback: () => { this._togglePerfOverlay() },
      },
    ])

    // React HUD → Phaser bridge: tower selection
    const onSelectTower = (e: Event): void => {
      this.selectedTowerId = (e as CustomEvent<{ towerId: string | null }>).detail.towerId
    }
    // React HUD → Phaser bridge: unit sending
    const onSendUnit = (e: Event): void => {
      if (!this.engine) return
      const { unitId } = (e as CustomEvent<{ unitId: string }>).detail
      const units = this.gameData?.units ?? []
      const unitCard = units.find((u) => u.id === unitId)
      if (unitCard === undefined) { console.warn('[MatchScene] Unit not found:', unitId, '— units loaded:', units.length); return }
      const ok = this.engine.sendUnit(this.localPlayerId, unitCard)
      if (!ok) console.warn('[MatchScene] sendUnit returned false for', unitId)
    }
    const onRotate = (): void => { if (this.gridRenderer) this.gridRenderer.rotateStep() }
    const onDoUpgrade = (e: Event): void => {
      if (!this.engine) return
      const { gx, gy, chosenPath } = (e as CustomEvent<{gx:number;gy:number;chosenPath?:'A'|'B'}>).detail
      if (chosenPath !== undefined) {
        this.engine.upgradeTowerPath(this.localPlayerId, gx, gy, chosenPath)
      } else {
        this.engine.upgradeTower(this.localPlayerId, gx, gy)
      }
    }
    const onUpgradeBase = (): void => { if (this.engine) this.engine.upgradeBase(this.localPlayerId) }
    const onDemolish = (e: Event): void => {
      if (!this.engine) return
      const { gx, gy } = (e as CustomEvent<{gx:number;gy:number}>).detail
      this.engine.demolishTower(this.localPlayerId, gx, gy)
    }
    const onToggleSpy = (): void => { this.toggleSpyMode() }
    const onToggleSound = (): void => { soundManager.setEnabled(!soundManager.enabled) }
    const onOpenPackScene = (e: Event): void => {
      const { cards, packType } = (e as CustomEvent<{ cards: unknown; packType: string }>).detail
      this.scene.launch('PackOpenScene', { cards, packType })
    }
    const onSurrender = (): void => {
      if (this.engine) {
        // Forfeit: immediately end the match as a defeat for the local player.
        // endMatch('defeat') triggers MATCH_END which cascades to onMatchEnd().
        this.engine.endMatch('defeat')
      }
    }
    // PvP: opponent closed their connection — end match so HP tiebreaker runs
    const onOpponentDisconnected = (): void => {
      if (!this._matchEnded && this.engine) {
        this.engine.endMatch('quit')
      }
    }
    window.addEventListener('ultimaze:select-tower', onSelectTower)
    window.addEventListener('ultimaze:send-unit', onSendUnit)
    window.addEventListener('ultimaze:rotate-camera', onRotate)
    window.addEventListener('ultimaze:do-upgrade', onDoUpgrade)
    window.addEventListener('ultimaze:upgrade-base', onUpgradeBase)
    window.addEventListener('ultimaze:demolish', onDemolish)
    window.addEventListener('ultimaze:toggle-spy', onToggleSpy)
    window.addEventListener('ultimaze:toggle-sound', onToggleSound)
    window.addEventListener('ultimaze:open-pack-scene', onOpenPackScene)
    window.addEventListener('ultimaze:surrender', onSurrender)
    window.addEventListener('ultimaze:opponent-disconnected', onOpponentDisconnected)

    // Range ring — dispatched by HUD on card hover / upgrade panel open.
    // gx/gy = -1 means "grid center" (used for unplaced card preview).
    const onShowRangeRing = (e: Event) => {
      const d = (e as CustomEvent<{ gx: number; gy: number; range: number }>).detail
      if (this.gridRenderer && this.engine) {
        const map = this.engine.config.map
        const gx = d.gx >= 0 ? d.gx : Math.floor(map.cols / 2)
        const gy = d.gy >= 0 ? d.gy : Math.floor(map.rows / 2)
        this.gridRenderer.setRangeRing({ gx, gy, range: d.range })
      }
    }
    const onHideRangeRing = () => {
      if (this.gridRenderer) this.gridRenderer.setRangeRing(null)
    }
    window.addEventListener('ultimaze:show-range-ring', onShowRangeRing)
    window.addEventListener('ultimaze:hide-range-ring', onHideRangeRing)

    // Chat relay — fallback for when __activeTransport.sendChat is unavailable
    const onSendChat = (e: Event) => {
      const text = (e as CustomEvent<{ text: string }>).detail?.text
      if (typeof text !== 'string' || !text.trim()) return
      const win = window as unknown as Record<string, unknown>
      const t = win['__activeTransport'] as { sendChat?: (s: string) => void } | null
      t?.sendChat?.(text.trim())
    }
    window.addEventListener('ultimaze:send-chat', onSendChat)

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('ultimaze:select-tower', onSelectTower)
      window.removeEventListener('ultimaze:send-unit', onSendUnit)
      window.removeEventListener('ultimaze:rotate-camera', onRotate)
      window.removeEventListener('ultimaze:do-upgrade', onDoUpgrade)
      window.removeEventListener('ultimaze:upgrade-base', onUpgradeBase)
      window.removeEventListener('ultimaze:demolish', onDemolish)
      window.removeEventListener('ultimaze:toggle-spy', onToggleSpy)
      window.removeEventListener('ultimaze:toggle-sound', onToggleSound)
      window.removeEventListener('ultimaze:open-pack-scene', onOpenPackScene)
      window.removeEventListener('ultimaze:surrender', onSurrender)
      window.removeEventListener('ultimaze:opponent-disconnected', onOpponentDisconnected)
      window.removeEventListener('ultimaze:show-range-ring', onShowRangeRing)
      window.removeEventListener('ultimaze:hide-range-ring', onHideRangeRing)
      window.removeEventListener('ultimaze:send-chat', onSendChat)
      // Clear active transport reference so HUD doesn't leak a closed socket
      ;(window as unknown as Record<string, unknown>)['__activeTransport'] = null
      // Stop music if scene is torn down while match is still running
      soundManager.stopMatchMusic()
    })

    // Spy overlay — drawn on top of the grid, visible only in spy mode
    this.spyOverlay = this.add.graphics().setDepth(1000)
    this.scale.on('resize', () => { this._drawSpyOverlay() })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.gridRenderer) return
      // pointer.x/.y are screen-space; transform to world-space for zoom/pan correctness
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y)
      const cell = this.gridRenderer.pointerToGrid(worldPoint.x, worldPoint.y)
      if (cell !== null) {
        this.gridRenderer.setHighlight(cell.gx, cell.gy)
      } else {
        this.gridRenderer.clearHighlight()
      }
    })
  }

  private _initEngine(
    mapId: string,
    deckId: string | null,
    towerStatOverrides: Record<string, TowerStatMultipliers> = {},
    botPersonality?: string,
    botTier?: number
  ): void {
    this._towerStatOverrides = towerStatOverrides
    // Tear down existing engine/renderers if re-initializing
    this._matchEnded = false
    if (this.engine) {
      this.engine.eventBus.off()
    }

    // Consume server-assigned player role (set by MatchmakingScreen for PvP;
    // falls back to 'player1' for bot matches).
    this.localPlayerId = consumeWindowPlayerId()

    const map = this.gameData.maps.find((m) => m.id === mapId) ?? this.gameData.maps[0]
    if (map === undefined) {
      throw new Error(`[MatchScene] Map not found: ${mapId}`)
    }

    const merchant = this.gameData.getHeroById('merchant')
    if (merchant === undefined) {
      throw new Error('[MatchScene] Hero "merchant" not found')
    }

    // Determine transport and opponent configuration based on game mode.
    // If MatchmakingScreen found a PvP match it stored the live WebSocketTransport
    // on window.__wsTransport. Consume it (one-shot) so no second scene can steal it.
    const bridge = getUIBridge()
    const isPvP = bridge.getGameMode() === 'pvp' && bridge.getMatchmakingMatchId() != null
    const wsTransport = consumeWindowTransport()

    let transport: ITransport
    let opponentIsBot: boolean

    if (isPvP && wsTransport !== null) {
      transport = wsTransport
      opponentIsBot = false
      // Derive opponent id from local role: player1's opponent is player2 and vice-versa
      this.opponentPlayerId = this.localPlayerId === 'player1' ? 'player2' : 'player1'
    } else {
      const availableUnitIds = this.gameData.units.map((u) => u.id)
      const availableTowerIds = this.gameData.towers.map((t) => t.id)
      transport = new LocalBotTransport(availableUnitIds, availableTowerIds, this.opponentPlayerId)
      opponentIsBot = true
      this.opponentPlayerId = 'bot1'
    }

    // Expose the active transport on window so HUD's ChatOverlay can send messages.
    // Use wsTransport directly (has sendChat) when available; otherwise expose null.
    ;(window as unknown as Record<string, unknown>)['__activeTransport'] = wsTransport ?? null

    // Build player deck from selected progression deck, or fall back to starter
    let playerDeck: Deck
    if (deckId !== null) {
      const savedDeck: Deck | undefined = getUIBridge().getDeck(deckId)
      if (savedDeck !== undefined) {
        // Validate that tower/unit ids exist in game data; filter out any unknown ids
        const resolvedTowerIds = (savedDeck.towers ?? []).filter(
          (id) => this.gameData.getTowerById(id) !== undefined
        )
        const resolvedUnitIds = (savedDeck.units ?? []).filter(
          (id) => this.gameData.getUnitById(id) !== undefined
        )
        playerDeck = {
          ...savedDeck,
          towers: resolvedTowerIds.length > 0 ? resolvedTowerIds : ['archer', 'firemage', 'frost', 'lightning'],
          units: resolvedUnitIds.length > 0 ? resolvedUnitIds : ['goblin'],
        }
      } else {
        playerDeck = {
          id: `${this.localPlayerId}-starter`,
          name: 'Starter Deck',
          iconElement: 'neutral',
          towers: ['archer', 'firemage', 'frost', 'lightning'],
          units: ['goblin'],
          heroId: 'merchant',
        }
      }
    } else {
      playerDeck = {
        id: `${this.localPlayerId}-starter`,
        name: 'Starter Deck',
        iconElement: 'neutral',
        towers: ['archer', 'firemage', 'frost', 'lightning'],
        units: ['goblin'],
        heroId: 'merchant',
      }
    }

    // Opponent deck: for PvP the real deck is unknown at match-start (server decides).
    // Use all available towers/units so the MatchEngine has valid card references.
    const opponentDeck: Deck = isPvP && !opponentIsBot
      ? {
          id: `${this.opponentPlayerId}-opponent`,
          name: 'Opponent Deck',
          iconElement: 'neutral',
          towers: this.gameData.towers.map((t) => t.id),
          units: this.gameData.units.map((u) => u.id),
          heroId: 'merchant',
        }
      : {
          id: 'bot1-starter',
          name: 'Bot Deck',
          iconElement: 'neutral',
          towers: this.gameData.towers.map((t) => t.id),
          units: this.gameData.units.map((u) => u.id),
          heroId: 'merchant',
        }

    const player1: Player = {
      id: this.localPlayerId,
      name: this.localPlayerId,
      deck: playerDeck,
      hero: merchant,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
    }

    const player2: Player = {
      id: this.opponentPlayerId,
      name: opponentIsBot ? pickBotName() : this.opponentPlayerId,
      deck: opponentDeck,
      hero: merchant,
      gold: 200,
      incomePerSec: 2,
      hp: 20,
      baseLvl: 1,
      grid: [],
      isBot: opponentIsBot,
    }

    // In PvP, skip random obstacles — each client would generate different layouts
    // (different Math.random() seeds) which breaks path agreement and tower placement.
    // Both clients use the fixed map JSON layout instead.
    const augmentedMap = (isPvP && wsTransport !== null)
      ? map
      : this._addRandomObstacles(map, 8)

    const matchConfig = {
      mode: '1v1' as const,
      players: [player1, player2],
      map: augmentedMap,
      transport,
      durationSecs: 900,
      localPlayerId: this.localPlayerId,
      forcedBotPersonality: botPersonality,
      forcedBotTier: botTier,
      getTowerStats: (id: string) => {
        const card = this.gameData.getTowerById(id)
        if (card === undefined) return undefined
        const ov = towerStatOverrides[id]
        if (ov === undefined) return card.stats
        return {
          ...card.stats,
          dmg: card.stats.dmg * ov.dmgMult,
          spd: card.stats.spd * ov.spdMult,
          range: card.stats.range * ov.rangeMult,
        }
      },
    }

    this.engine = new MatchEngine(matchConfig, this.gameData.elementMatrix)
    this.engine.start()

    // Start procedural ambient music keyed to the map's dominant element
    const mapElement = augmentedMap.elementBuffs[0]?.el ?? 'neutral'
    soundManager.startMatchMusic(mapElement)

    // ── Coop PvE: wire partner HP relay + partner-defeated handler ────────────
    // In coop mode the wsTransport consumed earlier is actually the PARTNER channel
    // (the main "opponent" bot is handled by LocalBotTransport inside MatchEngine).
    // We reuse wsTransport.sendCoopHp() to push local HP updates to the server and
    // listen to HP_CHANGED events on the EventBus to know when to relay.
    const coopMode = getUIBridge().getGameMode() === 'coop'
    if (coopMode && wsTransport instanceof WebSocketTransport) {
      this._coopWsTransport = wsTransport

      // Relay local HP changes to partner via server
      this.engine.eventBus.on('HP_CHANGED', (e) => {
        if (e.playerId === this.localPlayerId && this._coopWsTransport !== null) {
          const hp = (e.payload as Record<string, unknown>)['newHp'] as number
          this._coopWsTransport.sendCoopHp(hp)
          // Also update the partner's HUD store (reuse coopPartnerHp for symmetry).
          // The actual local hp is already tracked by useGameStore via HP_CHANGED.
        }
      })

      // When local player loses → inform server so partner knows
      this.engine.eventBus.on('MATCH_END', (e) => {
        const outcome = (e.payload as Record<string, unknown>)['outcome']
        if (outcome === 'lose' && this._coopWsTransport !== null) {
          this._coopWsTransport.sendCoopDefeat()
        }
      })

      // When partner is defeated → force local defeat
      const onPartnerDefeated = () => {
        if (this._matchEnded) return
        // Emit a synthetic MATCH_END so useGameStore transitions to 'ended'
        this.engine.eventBus.emit({
          type: 'MATCH_END',
          playerId: this.localPlayerId,
          payload: {
            outcome: 'lose',
            winner: null,
            reason: 'coop_partner_defeated',
          },
          timestamp: Date.now(),
        })
      }
      window.addEventListener('ultimaze:coop-partner-defeated', onPartnerDefeated)
      this._coopPartnerDefeatedCleanup = () => {
        window.removeEventListener('ultimaze:coop-partner-defeated', onPartnerDefeated)
      }

      // Wire server-driven wave-bot UNIT_SENT events into the local MatchEngine.
      // The WebSocketTransport receives GAME_EVENT relays from the server (wave-bot
      // sends these) and forwards them via onReceive.  In coop mode we use the
      // wsTransport to feed the engine's opponent bot channel so wave units appear.
      wsTransport.onReceive((event) => {
        if (event.type === 'UNIT_SENT') {
          const payload = event.payload as Record<string, unknown>
          if (payload['isCoopWave'] === true) {
            // Forward the wave-unit event into MatchEngine as if it came from the opponent bot
            const unitId = payload['unitId'] as string
            if (typeof unitId === 'string') {
              const unitCard = this.gameData.getUnitById(unitId)
              if (unitCard !== undefined) {
                // Add gold to opponent (wave-bot) so spend() succeeds in sendUnit()
                this.engine.addGoldForPlayer(this.opponentPlayerId, unitCard.cost)
                this.engine.sendUnit(this.opponentPlayerId, unitCard)
              }
            }
          }
        }
      })

      // ── Server coop wave-start overlay ────────────────────────────────────
      const onCoopWaveStart = (e: Event) => {
        const { wave, total } = (e as CustomEvent<{ wave: number; total: number }>).detail
        this._showCoopWaveOverlay(wave, total)
      }
      window.addEventListener('ultimaze:coop-wave-start', onCoopWaveStart)
      this._coopWaveStartCleanup = () => {
        window.removeEventListener('ultimaze:coop-wave-start', onCoopWaveStart)
      }

      // ── Server coop victory ────────────────────────────────────────────────
      const onCoopVictory = () => {
        if (this._matchEnded) return
        this._matchEnded = true
        this.engine.eventBus.emit({
          type: 'MATCH_END',
          playerId: this.localPlayerId,
          payload: { outcome: 'win', winner: this.localPlayerId, reason: 'coop_all_waves_cleared' },
          timestamp: Date.now(),
        })
      }
      window.addEventListener('ultimaze:coop-victory', onCoopVictory)
      this._coopVictoryCleanup = () => {
        window.removeEventListener('ultimaze:coop-victory', onCoopVictory)
      }
    } else {
      // Cleanup any previous coop listeners if mode changed
      if (this._coopPartnerDefeatedCleanup !== null) {
        this._coopPartnerDefeatedCleanup()
        this._coopPartnerDefeatedCleanup = null
      }
      if (this._coopWaveStartCleanup !== null) {
        this._coopWaveStartCleanup()
        this._coopWaveStartCleanup = null
      }
      if (this._coopVictoryCleanup !== null) {
        this._coopVictoryCleanup()
        this._coopVictoryCleanup = null
      }
      this._coopWsTransport = null
    }

    // Wire QuestSystem to the new engine's EventBus so quest progress is tracked (#10)
    if (this.questSystem !== null) {
      this.questSystem.subscribeToEventBus(this.engine.eventBus)
    }

    // Reset wave timer countdown to initial bot interval
    this.waveTimerMs = this.waveIntervalMs

    // Sync wave timer to actual bot unit sends
    this.engine.eventBus.on('UNIT_SENT', (e) => {
      if ((e.payload as Record<string, unknown>)['isBot'] === true) {
        this.waveTimerMs = this.waveIntervalMs
      }
    })

    // Cancel the match-reminder notification — the player is now in a match.
    try {
      NotificationService.getInstance().cancelMatchReminder().catch(() => {})
    } catch { /* NotificationService not initialised — ignore */ }

    // Re-init renderers
    if (this.gridRenderer) this.gridRenderer.destroy()
    if (this.towerRenderer) this.towerRenderer.destroy()
    if (this.unitRenderer) this.unitRenderer.destroy()
    if (this.projectileRenderer) this.projectileRenderer.destroy()

    this.gridRenderer = new GridRenderer(this, this.engine, this.localPlayerId)
    this.towerRenderer = new TowerRenderer(this, this.engine, this.localPlayerId, this.gridRenderer)
    this.unitRenderer = new UnitRenderer(this, this.engine, this.localPlayerId, this.gridRenderer)
    this.projectileRenderer = new ProjectileRenderer(this)

    // Reset projectile state on match re-init
    this.projectiles = []
    this.towerLastShotCache.clear()

    getUIBridge().setEventBus(this.engine.eventBus, 20, this.localPlayerId)

    // Notify HUD with the resolved tower/unit card objects and active hero for this match
    const deckTowerCards: TowerCard[] = playerDeck.towers
      .map((id) => this.gameData.getTowerById(id))
      .filter((c): c is TowerCard => c !== undefined)
    const deckUnitCards: UnitCard[] = playerDeck.units
      .map((id) => this.gameData.getUnitById(id))
      .filter((c): c is UnitCard => c !== undefined)
    const deckHero = this.gameData.getHeroById(playerDeck.heroId) ?? merchant
    window.dispatchEvent(new CustomEvent('ultimaze:deck-loaded', {
      detail: { towers: deckTowerCards, units: deckUnitCards, hero: deckHero },
    }))

    this.engine.eventBus.on('TOWER_PLACED',        () => soundManager.playTowerPlace())
    this.engine.eventBus.on('TOWER_UPGRADED',      () => soundManager.playTowerUpgrade())
    this.engine.eventBus.on('UNIT_SENT',           () => soundManager.playUnitSent())
    this.engine.eventBus.on('UNIT_KILLED',         (event) => {
      soundManager.playUnitDied()
      // Floating kill-gold text — only shown when the killed unit died on the LOCAL
      // player's grid (i.e. a bot unit was killed by the player's towers, earning gold).
      // killedOnPlayerId identifies whose grid the unit was on when killed.
      const p = event.payload as Record<string, unknown>
      if (!this.spyMode && p['killedOnPlayerId'] === this.localPlayerId) {
        const reward = typeof p['reward'] === 'number' ? p['reward'] : 0
        const gx = typeof p['gx'] === 'number' ? p['gx'] : -1
        const gy = typeof p['gy'] === 'number' ? p['gy'] : -1
        if (reward > 0 && gx >= 0 && gy >= 0 && this.gridRenderer) {
          const { cellW, cellH, originX, originY } = this.gridRenderer
          const iso = cartToIso(gx, gy, cellW, cellH)
          const sx = iso.x + originX
          const sy = iso.y + originY
          const floatText = this.add.text(sx, sy, `+${reward} ${i18n.t('PHASER_GOLD_UNIT')}`, {
            fontSize: '16px',
            color: '#FFD700',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 3,
          }).setDepth(20).setOrigin(0.5, 1.0)
          this.tweens.add({
            targets: floatText,
            y: sy - 60,
            alpha: 0,
            duration: 1200,
            ease: 'Power2',
            onComplete: () => { floatText.destroy() },
          })
        }
      }
    })
    this.engine.eventBus.on('UNIT_REACHED_BASE',   () => soundManager.playUnitReachedBase())
    this.engine.eventBus.on('SPELL_CAST',          () => soundManager.playSpellCast())
    this.engine.eventBus.on('MATCH_END', (event) => {
      this._matchEnded = true
      soundManager.stopMatchMusic()
      const outcome = (event.payload as Record<string, unknown>)['outcome']
      if (outcome === 'win') soundManager.playMatchWin()
      else if (outcome === 'lose') soundManager.playMatchLose()
      this.onMatchEnd()
    })
  }

  toggleSpyMode(): void {
    this.spyMode = !this.spyMode

    const viewPlayerId = this.spyMode ? this.opponentPlayerId : this.localPlayerId

    if (this.spyMode) {
      // Switch grid, tower, and unit renderer to opponent view and lock interaction
      if (this.gridRenderer) this.gridRenderer.setPlayerId(viewPlayerId)
      if (this.towerRenderer) this.towerRenderer.setPlayerId(viewPlayerId)
      if (this.unitRenderer) this.unitRenderer.setViewPlayerId(viewPlayerId)
      if (this.touchInput) this.touchInput.disable()
    } else {
      // Restore own view and re-enable interaction
      if (this.gridRenderer) this.gridRenderer.setPlayerId(viewPlayerId)
      if (this.towerRenderer) this.towerRenderer.setPlayerId(viewPlayerId)
      if (this.unitRenderer) this.unitRenderer.setViewPlayerId(viewPlayerId)
      if (this.touchInput) this.touchInput.enable()
    }

    this._drawSpyOverlay()

    // Notify HUD so it can update the button and SPY label
    window.dispatchEvent(new CustomEvent('ultimaze:spy-changed', { detail: { spyMode: this.spyMode } }))
  }

  private _drawSpyOverlay(): void {
    if (!this.spyOverlay) return
    this.spyOverlay.clear()
    if (!this.spyMode) return

    const w = this.scale.width
    const h = this.scale.height
    const t = 4 // border thickness

    // Red rectangular border around the canvas
    this.spyOverlay.fillStyle(0xff0000, 0.75)
    this.spyOverlay.fillRect(0, 0, w, t)         // top
    this.spyOverlay.fillRect(0, h - t, w, t)     // bottom
    this.spyOverlay.fillRect(0, 0, t, h)         // left
    this.spyOverlay.fillRect(w - t, 0, t, h)     // right
  }

  update(time: number, delta: number): void {
    if (!this.engine) return
    if (this._matchEnded) return

    PerformanceMonitor.begin('engine.update')
    this.engine.update(delta)
    PerformanceMonitor.end('engine.update')

    PerformanceMonitor.begin('render.grid')
    if (this.gridRenderer) this.gridRenderer.update()
    PerformanceMonitor.end('render.grid')

    PerformanceMonitor.begin('render.towers')
    if (this.towerRenderer) this.towerRenderer.update()
    PerformanceMonitor.end('render.towers')

    PerformanceMonitor.begin('render.units')
    if (this.unitRenderer) this.unitRenderer.update()
    PerformanceMonitor.end('render.units')

    // ── Adaptive quality: sample FPS every 2 s, switch quality when needed ──
    this._qualityFrameCount++
    this._qualityFpsAccum += 1000 / Math.max(delta, 1)
    if (time - this._qualityLastSample > 2000 && this._qualityFrameCount > 0) {
      const fps = this._qualityFpsAccum / this._qualityFrameCount
      const newQuality: 'full' | 'compact' =
        fps < 28 ? 'compact' : fps > 50 ? 'full' : this._renderQuality
      if (newQuality !== this._renderQuality) {
        this._renderQuality = newQuality
        this.unitRenderer?.setQuality(newQuality)
      }
      this._qualityFrameCount = 0
      this._qualityFpsAccum = 0
      this._qualityLastSample = time
    }
    if (this.gridRenderer && this.projectileRenderer) {
      this._spawnProjectilesFromLastShot()
      this._updateProjectiles(delta)
    }
    getUIBridge().tickRemainingMs(this.engine.getRemainingMs())

    // Emit wave timer countdown for HUD (Feature 4)
    this.waveTimerMs -= delta
    if (this.waveTimerMs <= 0) {
      this.waveTimerMs = this.waveIntervalMs
    }
    window.dispatchEvent(new CustomEvent('ultimaze:next-wave-timer', {
      detail: { remainingMs: Math.max(0, this.waveTimerMs) },
    }))

    // ── FPS / frame-time overlay ─────────────────────────────────────────────
    if (this._perfVisible) {
      this._frameCount++
      this._fpsAccum += 1000 / delta
      if (time - this._lastFpsSample > 1000) {
        const fps = this._fpsAccum / this._frameCount
        this._frameCount = 0
        this._fpsAccum = 0
        this._lastFpsSample = time
        if (this._perfOverlay) {
          const unitCount = this.engine.getAllUnits?.()?.length ?? 0
          this._perfOverlay.setText(
            `FPS: ${fps.toFixed(0)} | Units: ${unitCount} | delta: ${delta.toFixed(1)}ms`
          )
        }
      }
    }
  }

  /** Toggle the in-game FPS / frame-time performance overlay on or off. */
  private _togglePerfOverlay(): void {
    this._perfVisible = !this._perfVisible
    if (this._perfVisible) {
      if (this._perfOverlay === null) {
        this._perfOverlay = this.add.text(8, 8, 'FPS: -- | Units: -- | delta: --ms', {
          fontSize: '12px',
          color: '#ffffff',
          fontFamily: 'monospace',
          backgroundColor: '#00000088',
          padding: { x: 4, y: 2 },
        })
        this._perfOverlay.setDepth(100)
        this._perfOverlay.setScrollFactor(0)
      }
      this._perfOverlay.setVisible(true)
      // Reset accumulators so the first sample is fresh
      this._frameCount = 0
      this._fpsAccum = 0
      this._lastFpsSample = this.time.now
    } else {
      if (this._perfOverlay !== null) {
        this._perfOverlay.setVisible(false)
      }
    }
  }

  // ── Projectile helpers ────────────────────────────────────────────────────

  /**
   * Detect newly-fired shots by comparing each attack tower's lastShot to the
   * cached value from the previous frame. When a change is detected, find the
   * nearest alive enemy unit on that player's field and spawn a visual projectile.
   * This is purely cosmetic — engine damage has already been applied.
   */
  private _spawnProjectilesFromLastShot(): void {
    const { cellW, cellH, originX, originY } = this.gridRenderer

    for (const player of this.engine.config.players) {
      const grid = this.engine.getGrid(player.id)
      if (grid === undefined) continue

      // getTowers() is O(n placed towers) — replaces the old O(cols×rows) cell scan.
      const attackTowers = grid.getTowers().filter(t => t.cat === 'attack' && (t.projSpeed ?? 0) > 0)
      if (attackTowers.length === 0) continue

      const units = this.engine.getActiveUnits(player.id)
      const aliveUnits: typeof units = []
      for (const u of units) { if (u.alive) aliveUnits.push(u) }

      for (const tower of attackTowers) {
        // Only towers with a projectile speed defined get visual projectiles
        const projSpeed = tower.projSpeed
        if (projSpeed === undefined || projSpeed <= 0) continue

        const cached = this.towerLastShotCache.get(tower.instanceId) ?? 0
        if (tower.lastShot <= cached) continue  // no new shot this frame

        // Record updated lastShot
        this.towerLastShotCache.set(tower.instanceId, tower.lastShot)

        // Pick nearest alive unit to target (same logic the engine uses)
        if (aliveUnits.length === 0) continue
        const nearest = aliveUnits.reduce((best, u) => {
          const dx = u.gx - tower.gx
          const dy = u.gy - tower.gy
          const dxB = best.gx - tower.gx
          const dyB = best.gy - tower.gy
          return (dx * dx + dy * dy) < (dxB * dxB + dyB * dyB) ? u : best
        })

        // Convert grid coords to isometric screen coords
        const towerIso = cartToIso(tower.gx, tower.gy, cellW, cellH)
        const fromX = towerIso.x + originX
        const fromY = towerIso.y + originY

        const unitIso = cartToIso(nearest.gx, nearest.gy, cellW, cellH)
        // Match UnitRenderer's own-unit vertical offset so the projectile aims correctly
        const isMine = nearest.ownerId === this.localPlayerId
        const toX = unitIso.x + originX
        const toY = unitIso.y + originY + (isMine ? -(cellH * 0.6) : 0)

        const projectile: Projectile = {
          id: `${tower.instanceId}-${nearest.id}-${tower.lastShot}`,
          fromX,
          fromY,
          toX,
          toY,
          curX: fromX,
          curY: fromY,
          speed: projSpeed,
          towerId: tower.instanceId,
          towerTypeId: tower.id,
          targetUnitId: nearest.id,
          el: tower.el,
          hit: false,
          ownerId: player.id,
        }

        this.projectiles.push(projectile)
      }
    }
  }

  /**
   * Advance all active projectiles toward their target position each frame.
   * Projectiles are removed when they arrive (or when they're already marked hit).
   */
  private _updateProjectiles(delta: number): void {
    const dt = delta / 1000  // convert ms → seconds

    this.projectiles = this.projectiles.filter(p => {
      if (p.hit) return false

      const dx = p.toX - p.curX
      const dy = p.toY - p.curY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const step = p.speed * dt

      if (dist <= step + 2) {
        // Arrived — mark as hit and remove
        p.hit = true
        return false
      }

      const factor = step / dist
      p.curX += dx * factor
      p.curY += dy * factor
      return true
    })

    const viewPlayerId = this.spyMode ? this.opponentPlayerId : this.localPlayerId
    this.projectileRenderer.render(this.projectiles.filter(p => p.ownerId === viewPlayerId))
  }

  /**
   * Generate up to `count` additional random obstacle positions for a map.
   * Each candidate is validated: it must not sit on spawn/base/existing obstacles,
   * must be in the interior of the grid, and must not break A* connectivity.
   * Returns a new MapConfig with the augmented obstacle list. (#13)
   */
  private _addRandomObstacles(map: MapConfig, count: number): MapConfig {
    const existingSet = new Set<string>(map.obstacles.map(o => `${o.gx},${o.gy}`))
    const added: GridCoord[] = []

    for (let attempt = 0; added.length < count; attempt++) {
      // Limit attempts to avoid infinite loop on very crowded maps
      if (attempt >= count * 20) break

      const gx = 1 + Math.floor(Math.random() * (map.cols - 2))
      const gy = 1 + Math.floor(Math.random() * (map.rows - 2))
      const key = `${gx},${gy}`

      // Skip if it conflicts with spawn, base, or existing obstacles
      if (gx === map.spawnX && gy === map.spawnY) continue
      if (gx === map.baseX  && gy === map.baseY)  continue
      if (existingSet.has(key)) continue

      // Tentatively add the obstacle and check path connectivity
      existingSet.add(key)
      const allObstacles = [...map.obstacles, ...added, { gx, gy }]
      const blockedSet = new Set<string>(allObstacles.map(o => `${o.gx},${o.gy}`))

      const pathGrid = {
        cols: map.cols,
        rows: map.rows,
        isWalkable: (x: number, y: number): boolean => {
          if (x === map.spawnX && y === map.spawnY) return true
          if (x === map.baseX  && y === map.baseY)  return true
          return !blockedSet.has(`${x},${y}`)
        },
      }

      const path = findPath(
        pathGrid,
        { gx: map.spawnX, gy: map.spawnY },
        { gx: map.baseX,  gy: map.baseY  }
      )

      if (path !== null) {
        // Path still exists — keep this obstacle
        added.push({ gx, gy })
      } else {
        // Would block the path — reject and remove from set
        existingSet.delete(key)
      }
    }

    if (added.length === 0) return map

    return {
      ...map,
      obstacles: [...map.obstacles, ...added],
    }
  }

  private onMatchEnd(): void {
    // Exit spy mode so the canvas shows the local player's field on the result screen.
    if (this.spyMode) {
      this.toggleSpyMode()
    }

    // Persist quest progress immediately so the progression store is up-to-date
    // before the React MatchResult screen reads it.
    if (this.questSystem) {
      const states = this.questSystem.getAllStates()
      getUIBridge().updateQuestProgress(
        states.map((s) => ({
          questId: s.definitionId,
          progress: s.progress,
          completed: s.completed,
          claimedReward: s.claimedAt !== undefined,
        }))
      )
    }

    // Schedule a match re-engagement reminder ~23 hours from now.
    try {
      const elapsedMs = this.engine ? this.engine.getElapsedMs?.() ?? Date.now() : Date.now()
      NotificationService.getInstance()
        .scheduleMatchReminder(elapsedMs)
        .catch(() => {})
    } catch { /* NotificationService not initialised — ignore */ }

    // Do NOT restart BootScene here. Restarting BootScene calls setEventBus on the
    // new engine which resets matchPhase to 'running', racing with React's useEffect
    // that navigates to 'match-result' on phase === 'ended'. The Phaser update loop
    // is already halted by _matchEnded = true. When the player starts the next match
    // (via 'ultimaze:start-match'), _initEngine() re-initialises everything cleanly.
  }

  shutdown(): void {
    if (this.engine) {
      this.engine.eventBus.off()
    }
    if (this.gridRenderer) {
      this.gridRenderer.destroy()
    }
    if (this.towerRenderer) {
      this.towerRenderer.destroy()
    }
    if (this.unitRenderer) {
      this.unitRenderer.destroy()
    }
    if (this.projectileRenderer) {
      this.projectileRenderer.destroy()
    }
    this.projectiles = []
    this.towerLastShotCache.clear()
    if (this.touchInput) {
      this.touchInput.destroy()
    }
    if (this.keyboardInput) {
      this.keyboardInput.destroy()
    }
    if (this.spyOverlay) {
      this.spyOverlay.destroy()
    }
    // Clean up coop wave DOM listeners
    if (this._coopWaveStartCleanup !== null) {
      this._coopWaveStartCleanup()
      this._coopWaveStartCleanup = null
    }
    if (this._coopVictoryCleanup !== null) {
      this._coopVictoryCleanup()
      this._coopVictoryCleanup = null
    }
    if (this._coopWaveOverlay !== null) {
      this._coopWaveOverlay.destroy(true)
      this._coopWaveOverlay = null
    }
  }

  /**
   * Show a brief Phaser overlay announcing the next coop wave number.
   * The overlay fades in, stays for 2 s, then fades out automatically.
   */
  private _showCoopWaveOverlay(wave: number, total: number): void {
    // Destroy any previous overlay still on screen
    if (this._coopWaveOverlay !== null) {
      this._coopWaveOverlay.destroy(true)
      this._coopWaveOverlay = null
    }

    const cam = this.cameras.main
    const cx = cam.width / 2
    const cy = cam.height * 0.35

    const bg = this.add.graphics()
    bg.fillStyle(0x000000, 0.65)
    bg.fillRoundedRect(-180, -36, 360, 72, 12)

    const label = this.add.text(0, -8, `WELLE ${wave} / ${total}`, {
      fontFamily: 'monospace',
      fontSize: '22px',
      color: '#4AAA3A',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5)

    const sub = this.add.text(0, 18, 'Vorbereitet euch!', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#cccccc',
    }).setOrigin(0.5)

    const container = this.add.container(cx, cy, [bg, label, sub])
    container.setDepth(1000)
    container.setAlpha(0)
    this._coopWaveOverlay = container

    // Fade in → stay 2 s → fade out
    this.tweens.add({
      targets: container,
      alpha: { from: 0, to: 1 },
      duration: 300,
      onComplete: () => {
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: container,
            alpha: { from: 1, to: 0 },
            duration: 500,
            onComplete: () => {
              if (this._coopWaveOverlay === container) {
                container.destroy(true)
                this._coopWaveOverlay = null
              }
            },
          })
        })
      },
    })
  }
}
